import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import * as routes from "../../routes";
import * as style from "../../../styles/style.css.classes";
import * as FA from "../../../styles/fontawesome.css.classes";
import {
  b2fileid,
  enrollment_status,
  enrollmentid,
  studentid,
} from "../../../sql/classy/types";
import currency from "currency.js";
import { LocalDate } from "@js-joda/core";
import { renderFirstNameLastName, wrapInParentheses } from "../../render";
import { dyn } from "trader.ts/ui";
import { renderSaveButton } from "../../client/savebutton";
import { rpc } from "trader.ts/network";
import { match } from "ts-pattern";
import { mapPartial } from "trader.ts/utils";

export function render(opts: {
  allStudentsIncludingStopped: {
    id: studentid;
    firstname: string;
    lastname: string;
    enrollmentid: enrollmentid;
    inv_amount_total: currency | null;
    inv_amount_left: currency | null;
    inv_amount_paid: currency | null;
    status: enrollment_status;
  }[];
  certificates: {
    enrollmentid: enrollmentid;
    created_on: LocalDate;
    storagefileid: b2fileid;
  }[];
}): HTMLElement | HTMLElement[] {
  const selectedEnrollmentIds: Source<enrollmentid[]> = new Source([]);

  function selectAll() {
    const selectableIds = mapPartial(
      opts.allStudentsIncludingStopped.filter(
        (stu) => stu.status === "ActiveEnrollment",
      ),
      (stu) => {
        const status = getCertificateStatus(opts.certificates, stu);
        return status.tag === "ready" ? status.enrollmentid : null;
      },
    );

    const currentlySelected = selectedEnrollmentIds.get();

    if (selectableIds.length === currentlySelected.length) {
      selectedEnrollmentIds.set([]);
    } else {
      selectedEnrollmentIds.set(selectableIds);
    }
  }

  const cellStyle = `padding: 4px 4px 4px; vertical-align: middle;`;

  return (
    <div>
      <section>
        <table>
          <tr>
            <th>{"Student"}</th>
            <th style="width: 240px;">
              {"Attest" + " "}
              <a onclick={() => selectAll()}>
                {wrapInParentheses("Alles selecteren")}
              </a>
            </th>
          </tr>

          {opts.allStudentsIncludingStopped
            .filter((stu) => stu.status === "ActiveEnrollment")
            .orderBy((stu) => renderFirstNameLastName(stu))
            .map((stu) => {
              const status = getCertificateStatus(opts.certificates, stu);

              return (
                <tr>
                  <td style={cellStyle}>
                    <a
                      href={routes.studentOverviewRoute.link({
                        studentid: stu.id,
                      })}
                    >
                      {renderFirstNameLastName(stu)}
                    </a>
                  </td>
                  <td style={cellStyle}>
                    {renderCertificateStatus(selectedEnrollmentIds, status)}
                  </td>
                </tr>
              );
            })}
        </table>
      </section>

      {dyn(selectedEnrollmentIds, (ids) => {
        if (ids.length === 0) {
          return <span></span>;
        } else {
          const button = renderSaveButton({
            save: function () {
              return rpc(
                routes.makeMutualityCertificatesSpec,
                undefined,
                { enrollmentids: ids },
                { timeoutMS: 120000 },
              );
            },
            reload: true,
          });
          return (
            <div style="display: flex; justify-content: flex-end;">
              <div className={style.panel} style="max-width: 480px;">
                <article>
                  {"Je zal %n attesten aanmaken. Voor elk attest zetten we een email in de wachtrij. Voor elk attest wordt een PDF aangemaakt, dus dit kan even duren.".replace("%n", ids.length.toString())}
                </article>
                <div style="text-align: right;">{button}</div>
              </div>
            </div>
          );
        }
      })}
    </div>
  );
}

function handleClick(
  selectedEnrollmentIds: Source<enrollmentid[]>,
  enrollmentid: enrollmentid,
): void {
  const curr = selectedEnrollmentIds.get();
  if (curr.includes(enrollmentid)) {
    selectedEnrollmentIds.set(curr.filter((id) => id !== enrollmentid));
  } else {
    selectedEnrollmentIds.set(curr.concat(enrollmentid));
  }
}

type certificateStatus =
  | { tag: "unpaid" }
  | { tag: "ready"; enrollmentid: enrollmentid }
  | { tag: "created"; cert: { enrollmentid: enrollmentid } };

function renderCertificateStatus(
  selectedEnrollmentIds: Source<enrollmentid[]>,
  status: certificateStatus,
): HTMLElement {
  return match(status)
    .with({ tag: "unpaid" }, () => (
      <span className={style.input_like} style="margin-bottom: 0px;">
        <b>{"Nog niet volledig betaald"}</b>
      </span>
    ))
    .with({ tag: "created" }, ({ cert }) => (
      <a
        href={routes.downloadMutualityCertificateSpec.route.link({
          enrollmentid: cert.enrollmentid,
        })}
        download="certificate.pdf"
        className={style.input_like}
        style="margin-bottom: 0px; text-decoration: underline;"
      >
        {"Attest aangemaakt"}
      </a>
    ))
    .with({ tag: "ready" }, ({ enrollmentid }) => (
      <div>
        {dyn(selectedEnrollmentIds, (ids) => (
          <label
            className={style.checkbox + style.input_like}
            style="margin-bottom: 0px;"
          >
            <input
              type="checkbox"
              checked={ids.includes(enrollmentid) ? "true" : null}
              onclick={() => handleClick(selectedEnrollmentIds, enrollmentid)}
            />
            <div>{"Selecteer"}</div>
          </label>
        ))}
      </div>
    ))
    .exhaustive();
}

function getCertificateStatus(
  certificates: {
    enrollmentid: enrollmentid;
    created_on: LocalDate;
    storagefileid: b2fileid;
  }[],
  stu: {
    enrollmentid: enrollmentid;
    inv_amount_paid: currency | null;
    inv_amount_left: currency | null;
  },
): certificateStatus {
  const cert = certificates.find(
    (cert) => cert.enrollmentid === stu.enrollmentid,
  );
  return stu.inv_amount_paid === null ||
    stu.inv_amount_paid.intValue <= 0 ||
    (stu.inv_amount_left?.intValue || 0) > 0
    ? { tag: "unpaid" }
    : cert
    ? { tag: "created", cert: cert }
    : { tag: "ready", enrollmentid: stu.enrollmentid };
}
